/* eslint-disable react/prop-types */
import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Table,
  TableColumn,
  TableHeaderColumn,
} from '@alefeducation/ui-elements-library-core';
import sortBy from 'lodash/sortBy';
import { Share } from '@alefeducation/ui-elements-library-icons';
import AssignmentContext from '../../../common/AssignmentContext/AssignmentContext';
import { filterBySelectedTeam } from '../../helpers';
import { useStudentListContext } from '../StudentListContext';
import { useCommonSectionItemElements } from '../useCommonSectionItemElements';
import {
  evaluationDateColumn,
  fileColumn,
  gradeColumn,
  resubmissionColumn,
  studentColumn,
  submissionDateColumn,
} from './commonStudentSectionColumns';
import { NewStudentListSection } from './StudentListSection/NewStudentListSection';
import { actionColumnWidth } from './constants';

export const NewPendingReturnSubmissionsSection = () => {
  const { t } = useTranslation();
  const {
    groupedSubmissions,
    onViewStudent,
    setSubmissionsReturnModalDetails,
    setStudentSubmissionIdsToReturn,
    attachmentRequired,
    dueOn,
  } = useStudentListContext();
  const {
    assignmentData: { assignmentType },
  } = useContext(AssignmentContext);
  let { pendingReturn } = groupedSubmissions;
  const assignmentTypeForPendo = assignmentType
    .split('_')
    .join('-')
    .toLowerCase();
  const {
    filters: { selectedTeamMembersIds, selectedTeamFilter },
  } = useContext(AssignmentContext);

  pendingReturn =
    pendingReturn &&
    filterBySelectedTeam(
      selectedTeamFilter,
      selectedTeamMembersIds,
      pendingReturn,
    );

  const onReturnAll = e => {
    e.stopPropagation();
    setStudentSubmissionIdsToReturn(
      pendingReturn.map(({ submission }) => submission.id),
    );
    setSubmissionsReturnModalDetails({ isOpen: true });
  };

  const onReturnSingle = (submissionId, studentName) => e => {
    e.stopPropagation();
    setStudentSubmissionIdsToReturn([submissionId]);
    setSubmissionsReturnModalDetails({
      isOpen: true,
      studentName,
      isReturnIndividual: true,
    });
  };

  const sectionItems =
    pendingReturn &&
    sortBy(pendingReturn, ({ student }) => student.name).map(
      ({ student, submission, resubmission }) => ({
        ...student,
        submission,
        resubmission,
      }),
    );
  const sectionItemsTable =
    pendingReturn.length > 0 ? (
      <Table
        rows={sectionItems}
        columns={[
          studentColumn(t),
          { ...submissionDateColumn(dueOn, t) },
          { ...evaluationDateColumn(t) },
          { ...fileColumn(attachmentRequired, t, '126px') },
          { ...resubmissionColumn(t) },
          { ...gradeColumn(t, '364px') },
          {
            key: 'id',
            name: t('Actions'),
            renderColumn: ({ row }) => {
              const { feedbackField, viewIcon } = useCommonSectionItemElements(
                row,
                row.submission,
              );
              return (
                <TableColumn
                  row={row}
                  isFirstColumn={false}
                  className="actions-cell"
                >
                  <div className="actions-container">
                    <div
                      data-tip={t('Return to student')}
                      onClick={onReturnSingle(row.submission.id)}
                      data-testid={`return-submission-${row.submission.id}`}
                      className={`data-analytics-${assignmentTypeForPendo}-class-dashboard-return`}
                    >
                      <Share fontSize={18} />
                    </div>
                    {feedbackField}
                    {viewIcon(row, onViewStudent)}
                  </div>
                </TableColumn>
              );
            },
            renderHeaderColumn: rows => (
              <TableHeaderColumn
                isFirstColumn={false}
                rows={rows}
                width={actionColumnWidth}
              >
                <div className="col-heading">{t('Actions')}</div>
              </TableHeaderColumn>
            ),
            alignText: 'right',
            width: actionColumnWidth,
            height: '44px',
          },
        ]}
        isSticky={true}
        translationKeys={t}
      />
    ) : null;

  return (
    <NewStudentListSection
      data-testid="pending-return-section"
      submissions={pendingReturn}
      sectionName={t('To Be Returned')}
      labelColor={'#BCEAE9'}
      sectionItems={sectionItemsTable}
      groupControlItems={
        pendingReturn && pendingReturn.length ? (
          <div
            data-tip={t('Return All')}
            onClick={onReturnAll}
            className={`data-analytics-${assignmentTypeForPendo}-class-dashboard-section-group-return`}
          >
            <Share fontSize={18} />
          </div>
        ) : null
      }
    />
  );
};
